import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api';
import Icon from './Icon';

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');

// Visual punch card: filled dots = progress toward the goal.
const PunchDots = ({ filled, goal }) => (
  <div className="punch-dots" aria-label={`${filled} of ${goal}`}>
    {Array.from({ length: goal }).map((_, i) => (
      <span key={i} className={`punch-dot ${i < filled ? 'on' : ''}`}>
        {i < filled ? <Icon name="check" size={14} /> : null}
      </span>
    ))}
  </div>
);

const RewardsPanel = ({ showConfig = false }) => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ punch_goal: '', reward_label: '' });
  const [phone, setPhone] = useState('');
  const [result, setResult] = useState(null);
  const [ready, setReady] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const loadReady = useCallback(async () => {
    try { setReady(await api.loyaltyRewardsReady()); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadReady(); }, [loadReady]);

  const loadCfg = useCallback(async () => {
    try {
      const c = await api.loyaltyConfig();
      setCfg(c);
      setForm({ punch_goal: String(c.punch_goal), reward_label: c.reward_label });
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2200); };

  const saveCfg = async () => {
    setError('');
    try {
      const c = await api.loyaltySetConfig({
        punch_goal: parseInt(form.punch_goal, 10) || 3,
        reward_label: form.reward_label.trim() || 'Free Wash',
      });
      setCfg(c); flash('Saved');
    } catch (e) { setError(e.message); }
  };

  const toggle = async (on) => {
    try { setCfg(await api.loyaltySetConfig({ enabled: on })); flash(on ? 'Rewards turned on' : 'Rewards turned off'); }
    catch (e) { setError(e.message); }
  };

  const lookup = async (override) => {
    const p = (typeof override === 'string' ? override : phone).trim();
    setError(''); setResult(null);
    if (!p) { setError('Enter a phone number.'); return; }
    try { setResult(await api.loyaltyMember(p)); }
    catch (e) { setError(e.message); }
  };

  const redeem = async () => {
    setError('');
    try {
      const r = await api.loyaltyRedeem(phone.trim());
      setResult(await api.loyaltyMember(phone.trim()));
      loadReady();
      flash(r.message || 'Redeemed');
    } catch (e) { setError(e.message); }
  };

  const goal = result?.config?.punch_goal ?? cfg?.punch_goal ?? 3;
  const label = result?.config?.reward_label ?? cfg?.reward_label ?? 'Free Wash';

  return (
    <div className="rewards-panel">
      {showConfig && (
        <div className="rate-card">
          <h3><Icon name="gift" size={17} /> Rewards settings</h3>

          <label className="rw-toggle">
            <input type="checkbox" checked={!!cfg?.enabled} onChange={(e) => toggle(e.target.checked)} />
            <span>
              <strong>Rewards program {cfg?.enabled ? 'ON' : 'OFF'}</strong> — {cfg?.enabled
                ? `after ${cfg?.punch_goal} completed details, the customer earns a ${cfg?.reward_label}, then it resets`
                : 'no punches are awarded'}
            </span>
          </label>

          <div className="rw-config two">
            <label>Details required
              <input type="number" min="1" value={form.punch_goal} onChange={(e) => setForm({ ...form, punch_goal: e.target.value })} />
            </label>
            <label>Reward name
              <input type="text" placeholder="Free Wash" value={form.reward_label} onChange={(e) => setForm({ ...form, reward_label: e.target.value })} />
            </label>
          </div>
          <p className="admin-subtle">Earned automatically by phone number — customers don't need an account. They get a text after each detail.</p>
          <div className="path-actions">
            <button className="admin-primary-btn" onClick={saveCfg}>Save settings</button>
            {msg && <span className="path-msg">{msg}</span>}
          </div>
        </div>
      )}

      {ready && ready.customers && ready.customers.length > 0 && (
        <div className="rw-ready-card">
          <h3><Icon name="gift" size={17} /> {ready.customers.length} customer{ready.customers.length > 1 ? 's have' : ' has'} a {ready.reward_label} ready</h3>
          <p className="admin-subtle">Tap a customer to look them up and redeem when they come in.</p>
          <div className="rw-ready-list">
            {ready.customers.map((c) => (
              <button key={c.phone} className="rw-ready-item" onClick={() => { setPhone(c.phone); lookup(c.phone); }}>
                <span className="rw-ready-phone"><Icon name="phone" size={13} /> {c.phone}</span>
                <span className="rw-ready-count">{c.free_available > 1 ? `${c.free_available} ready` : '1 ready'}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rw-lookup-card">
        <h3>Look up a customer</h3>
        <p className="admin-subtle">Enter a phone number to see punch-card progress and redeem a reward.</p>
        <div className="rw-search">
          <input placeholder="Customer phone number" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookup()} />
          <button className="admin-primary-btn" onClick={lookup}><Icon name="search" size={15} /> Look up</button>
        </div>

        {error && <p className="admin-error"><Icon name="alert" size={15} /> {error}</p>}
        {msg && !error && !showConfig && <p className="deploy-ok">{msg}</p>}

        {result && (
          <div className="rw-result">
            <div className="punch-card">
              <div className="punch-head">
                <span>{result.member.punches} of {goal} details</span>
                {result.member.free_available > 0 && (
                  <span className="punch-free"><Icon name="gift" size={14} /> {result.member.free_available} {label}{result.member.free_available > 1 ? 's' : ''} ready</span>
                )}
              </div>
              <PunchDots filled={result.member.punches} goal={goal} />
              {result.member.free_available > 0 && (
                <button className="admin-primary-btn punch-redeem" onClick={redeem}><Icon name="gift" size={15} /> Redeem a {label}</button>
              )}
            </div>

            <div className="rw-stats three">
              <div className="rw-stat"><span>Progress</span><strong>{result.member.punches}/{goal}</strong></div>
              <div className="rw-stat accent"><span>{label}s ready</span><strong>{result.member.free_available}</strong></div>
              <div className="rw-stat"><span>Lifetime details</span><strong>{result.member.lifetime_details}</strong></div>
            </div>

            <div className="table-wrap">
              <table className="bookings-table">
                <thead><tr><th>Date</th><th>Activity</th></tr></thead>
                <tbody>
                  {result.transactions.map((t) => (
                    <tr key={t.id}>
                      <td data-label="Date">{fmt(t.created_at)}</td>
                      <td data-label="Activity">{t.type === 'reward' ? `🎁 ${t.description}` : t.type === 'redeem' ? `✅ ${t.description}` : t.description}</td>
                    </tr>
                  ))}
                  {!result.transactions.length && <tr><td colSpan={2} className="empty-row">No activity yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RewardsPanel;
